import React from 'react';
import { Poll, StackedLineChart, Science } from '@mui/icons-material'; // Added 'Science' for visual variety on the 3rd card
import { Box, Card, Grid2, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Skeleton } from 'antd';

// 1. Reusable Card Component for a cleaner, modern look
const StatCard = ({ title, value, subtitle, icon: IconComponent, color, loading }) => {
  if (loading) {
    return (
      <Box sx={{ p: 2, height: '100%', bgcolor: '#fff', borderRadius: '16px' }}>
        <Skeleton active paragraph={{ rows: 2 }} />
      </Box>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider', // Softer than hard gray
        background: 'linear-gradient(145deg, #ffffff, #fafbff)', // Very subtle depth
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
          transform: 'translateY(-5px)', // Gentle lift effect on hover
          boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.08)',
          borderColor: alpha(color, 0.3),
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ fontWeight: 600, letterSpacing: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
            {value}
          </Typography>
        </Box>

        {/* Modern Icon Container with soft matching background */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '14px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: alpha(color, 0.1),
            color: color,
          }}
        >
          <IconComponent fontSize="large" />
        </Box>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
        {subtitle}
      </Typography>
    </Card>
  );
};

export default function PersonnelStatCards({ facilityList, facilityLoading }) {
  // 2. Pre-calculate values so the rendering logic stays clean
  const totalFacilities = facilityList?.length || 0;

  // If no data and not loading, render nothing (keeping your original logic)
  if (!facilityLoading && totalFacilities < 1) return null;

  const activeFacilities = facilityList?.filter((x) => x.status === 'ACTIVE')?.length || 0;
  const activePercentage = totalFacilities > 0 ? (activeFacilities / totalFacilities) * 100 : 0;
  const totalSamplePoints =
    facilityList?.reduce((sum, item) => sum + (item?.samplePointCount || 0), 0) || 0;

  // Formatters
  const formatNumber = (num) => Intl.NumberFormat('en-NG', { style: 'decimal' }).format(num);
  const formatPercent = (num) =>
    Intl.NumberFormat('en-NG', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num) + '%';

  return (
    <Box>
      <Grid2 container spacing={3} alignItems="stretch">
        <Grid2 size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
          <StatCard
            loading={facilityLoading}
            title="FACILITIES"
            value={formatNumber(totalFacilities)}
            subtitle="Total No. of facilities"
            icon={Poll}
            color="#00ab55" // Success Green
          />
        </Grid2>

        <Grid2 size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
          <StatCard
            loading={facilityLoading}
            title="ACTIVE"
            value={formatPercent(activePercentage)}
            subtitle="Percentage of Active Facilities"
            icon={StackedLineChart}
            color="#ff4842" // Error Red
          />
        </Grid2>

        <Grid2 size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
          <StatCard
            loading={facilityLoading}
            title="SAMPLING POINTS"
            value={formatNumber(totalSamplePoints)}
            subtitle="Total Sampling Points across Facilities"
            icon={Science} // Changed icon so it's not a duplicate of the middle card
            color="#ffc107" // Warning Yellow
          />
        </Grid2>
      </Grid2>
    </Box>
  );
}
